<script lang="ts">
	import Button from "@components/inputs/Button.svelte";
	import MultiRangeSlider from "@components/inputs/MultiRangeSlider.svelte";
	import { useGroup } from "@components/utils/group.store";
	import { useAlgoliaClient } from "@stores/algolia";
	import {
		distance,
		employmentTypes,
		locationQuery,
		salary
	} from "@stores/searchQuery";
	import Location from "@svg/Location.svelte";
	import SchoolIcon from "@svg/SchoolIcon.svelte";
	import WorkHistoryIcon from "@svg/WorkHistoryIcon.svelte";
	import WorkIcon from "@svg/WorkIcon.svelte";
	import { Dialog } from "bits-ui";
	import { onMount } from "svelte";
	import { Drawer } from "vaul-svelte";
	import DrawerContent from "./DrawerContent.svelte";
	import DrawerHandle from "./DrawerHandle.svelte";
	import DrawerHeader from "./DrawerHeader.svelte";

	// Use a reactive variable to track screen size
	let large = $state(false);

	// Directly bind to store values

	// Function to check if screen is large
	function checkScreenSize() {
		large = window.matchMedia("(min-width: 1024px)").matches;
	}

	// Add event listener for screen resize
	onMount(() => {
		checkScreenSize();
		window.addEventListener("resize", checkScreenSize);

		return () => {
			window.removeEventListener("resize", checkScreenSize);
		};
	});

	const { selected, setValue } = useGroup<boolean>("filterSettingsDrawer");
	let { performSearch } = useAlgoliaClient();

	// Function to toggle employment type
	function toggleEmploymentType(index: number) {
		$employmentTypes[index].checked = !$employmentTypes[index].checked;
		$employmentTypes = [...$employmentTypes]; // Update the store to trigger reactivity
	}

	let drawerTriggerRef: HTMLElement | null = $state(null);
	let dialogTriggerRef: HTMLElement | null = $state(null);
	let drawerCloseRef: HTMLElement | null = $state(null);
	let dialogCloseRef: HTMLElement | null = $state(null);

	function open() {
		if (large) {
			dialogTriggerRef?.click();
		} else {
			drawerTriggerRef?.click();
		}
	}

	function close() {
		if (large) {
			dialogCloseRef?.click();
		} else {
			drawerCloseRef?.click();
		}
		performSearch();
	}

	selected.subscribe((value) => {
		if (value) {
			setTimeout(() => {
				open();
			}, 0);
		}
	});
</script>

{#snippet content()}
	<DrawerContent>
		<DrawerHandle />
		<DrawerHeader
			title="Filters"
			onClose={() => {
				close();
			}}
		/>

		<div class="flex w-full flex-col gap-4">
			<div class="mb-2 text-lg font-bold">Dienstverband</div>
			<div class="flex basis-full gap-2">
				{#each $employmentTypes as employmentType, index}
					<button
						onclick={() => {
							toggleEmploymentType(index);
						}}
						class="flex basis-1/4 flex-col items-center justify-center gap-2 rounded-lg border-2 p-2 text-center text-xs outline-none transition-all duration-300 md:p-4
                    {!employmentType.checked
							? 'opacity-50'
							: 'border-secondary bg-secondary font-bold text-white'}
                "
					>
						<span>
							{employmentType.name}
						</span>
						{#if employmentType.searchTerm === "FULL_TIME"}
							<WorkIcon class={employmentType.checked ? "fill-white" : ""} />
						{:else if employmentType.searchTerm === "PART_TIME"}
							<WorkIcon class={employmentType.checked ? "fill-white" : ""} />
						{:else if employmentType.searchTerm === "INTERN"}
							<SchoolIcon class={employmentType.checked ? "fill-white" : ""} />
						{:else if employmentType.searchTerm === "TEMPORARY"}
							<WorkHistoryIcon
								class={employmentType.checked ? "fill-white" : ""}
							/>
						{/if}
					</button>
				{/each}
			</div>

			<div class="mb-2 text-lg font-bold">Minimum salaris</div>

			<div class="mr-2">
				<MultiRangeSlider
					id="always"
					first="label"
					last="label"
					pipstep={2}
					pushy={true}
					pips
					prefix={"€"}
					step={250}
					gap={1000}
					max={5000}
					bind:values={$salary}
					range={"min"}
				/>
			</div>
			<div class="mb-2 text-lg font-bold">Maximale afstand</div>

			<div class="mr-2 {!$locationQuery && 'pointer-events-none opacity-30'}">
				<MultiRangeSlider
					id="always"
					first="label"
					last="label"
					step={5}
					min={10}
					suffix={"km"}
					bind:values={$distance}
					range={"min"}
				/>
			</div>
			{#if !$locationQuery}
				<div class="rounded-lg bg-blue-50 p-4 text-blue-800">
					<div class="flex items-center gap-2">
						<Location class="h-5 w-5 fill-blue-600" />
						<span class="font-medium">Locatie niet ingesteld</span>
					</div>
					<p class="mt-2 text-sm">
						De afstandsfilter werkt alleen als je een locatie hebt ingesteld.
						Stel je locatie in om vacatures in jouw buurt te vinden.
					</p>
				</div>
			{/if}
		</div>
	</DrawerContent>{/snippet}

{#if !large}
	<Drawer.Root
		onClose={() => {
			setTimeout(() => {
				setValue(null);
				performSearch();
			}, 200);
		}}
	>
		<Drawer.Trigger class="hidden">
			<div bind:this={drawerTriggerRef}></div>
		</Drawer.Trigger>
		<Drawer.Close class="hidden">
			<div bind:this={drawerCloseRef}></div>
		</Drawer.Close>

		<Drawer.Portal>
			<Drawer.Overlay class="fixed inset-0 z-[60] bg-black/80" />

			<Drawer.Content
				class="fixed bottom-0 left-0 right-0 z-[70] flex max-h-[85vh] flex-col rounded-t-[10px] bg-white"
			>
				<!-- Scrollable content area with padding at the bottom to prevent content from being hidden behind the footer -->
				<div class="overflow-y-auto pb-24">
					{#if $selected}
						{@render content()}
					{/if}
				</div>

				<!-- Fixed footer with action button -->
				<div
					class="fixed bottom-0 left-0 right-0 z-[71] border-t border-gray-200 bg-white p-4"
				>
					<Button
						variant="btn-contained"
						color="primary"
						textColor="text-white"
						type="button"
						onclick={() => {
							performSearch();
							close();
						}}
						css="w-full max-w-xl mx-auto"
					>
						Toepassen
					</Button>
				</div>
			</Drawer.Content>
		</Drawer.Portal>
	</Drawer.Root>
{:else}
	<Dialog.Root
		onOpenChange={(open) => {
			if (!open) {
				setValue(null);
				performSearch();
			}
		}}
	>
		<Dialog.Trigger class="hidden">
			<div bind:this={dialogTriggerRef}></div>
		</Dialog.Trigger>
		<Dialog.Close class="hidden" bind:ref={dialogCloseRef}></Dialog.Close>
		<Dialog.Portal>
			<Dialog.Overlay class="fixed inset-0 z-[60] bg-black/50 pl-80" />

			<Dialog.Content
				class="fixed left-[50%] top-[50%] z-[60] flex max-h-[85vh] w-[90vw] max-w-[800px] translate-x-[-50%] translate-y-[-50%] flex-col rounded-lg bg-white shadow-lg xl:left-[calc(50%+var(--spacing)*40)]"
			>
				{#if $selected}
					<!-- Scrollable content area -->
					<div class="overflow-y-auto p-6">
						{@render content()}
					</div>

					<!-- Fixed footer with action buttons -->
					<div
						class="mt-auto flex shrink-0 justify-end gap-4 border-t border-gray-200 p-6 pt-4"
					>
						<Button
							color="primary"
							variant="btn-text"
							type="button"
							onclick={() => {
								close();
							}}
						>
							Annuleren
						</Button>
						<Button
							variant="btn-contained"
							color="primary"
							type="button"
							onclick={() => {
								performSearch();
								close();
							}}
						>
							Toepassen
						</Button>
					</div>
				{/if}
			</Dialog.Content>
		</Dialog.Portal>
	</Dialog.Root>
{/if}
